import { createFileRoute } from "@tanstack/react-router";

import { CategoriaSeoPage, type CategoriaSeoConfig } from "@/components/site/CategoriaSeoPage";
import { productos } from "@/lib/productos";
import { crearHeadCategoriaSeo } from "@/lib/seo-categorias";

const modelos = productos.filter((producto) => producto.category === "tarjetas");

const config: CategoriaSeoConfig = {
  path: "/tarjeta-nfc-qr",
  breadcrumb: "Tarjeta NFC",
  eyebrow: "Tarjeta portátil NFC",
  title: "Tarjeta NFC para reseñas de Google",
  intro:
    "Lleva un acceso directo a las reseñas de tu negocio en la billetera. La tarjeta NFC de Pyme Visible se entrega configurada y no requiere mensualidades.",
  category: "tarjetas",
  featuredId: "tarjeta-google",
  benefits: [
    "Formato portátil de 8,5 × 5 cm",
    "Chip NFC configurado",
    "Sin aplicaciones ni mensualidades",
    "Lista para usar al recibirla",
  ],
  sectionLabel: "Siempre a mano",
  sectionTitle: "Pide reseñas donde atiendas.",
  sectionCopy:
    "La tarjeta NFC funciona bien cuando la atención no ocurre siempre frente a un mesón. Puedes acercarla al teléfono del cliente al terminar el servicio.",
  useCases: [
    {
      titulo: "Atención a domicilio",
      texto: "Solicita una reseña al terminar una visita, instalación o entrega fuera del local.",
    },
    {
      titulo: "Ventas presenciales",
      texto: "Llévala en la billetera y úsala cuando cierres una venta o prestes un servicio.",
    },
    {
      titulo: "Ferias y eventos",
      texto: "Comparte el perfil de Google sin pedir al cliente que busque el nombre del negocio.",
    },
    {
      titulo: "Equipos en terreno",
      texto: "Entrega una tarjeta a quien representa tu negocio y atiende directamente a clientes.",
    },
  ],
  faqs: [
    {
      pregunta: "¿Para qué sirve una tarjeta NFC?",
      respuesta:
        "Permite abrir un enlace al acercarla a un teléfono compatible. En este modelo, el enlace dirige al perfil de Google del negocio para facilitar que el cliente deje una reseña.",
    },
    {
      pregunta: "¿La tarjeta también tiene código QR?",
      respuesta:
        "No se incluye como característica comprometida del producto. La tarjeta se vende y se entrega como una tarjeta NFC configurada.",
    },
    {
      pregunta: "¿Qué tamaño y material tiene?",
      respuesta:
        "Mide 8,5 cm de alto por 5 cm de ancho y está fabricada en PVC tipo tarjeta bancaria.",
    },
    {
      pregunta: "¿Llega programada?",
      respuesta:
        "Sí. Confirmamos el enlace de tu negocio, configuramos el chip NFC, comprobamos su funcionamiento y la entregamos lista para usar.",
    },
    {
      pregunta: "¿Tiene cobro mensual?",
      respuesta:
        "No. Pagas por el producto configurado y no necesitas contratar una aplicación ni una mensualidad para utilizarlo.",
    },
  ],
  finalTitle: "Lleva tu tarjeta NFC contigo",
  finalCopy:
    "Envíanos el perfil de Google de tu negocio. Te confirmamos la configuración, el precio y la forma de entrega antes de preparar la tarjeta.",
  whatsappText:
    "Hola, vi la tarjeta NFC para reseñas de Google en Pyme Visible y quiero cotizar una para mi negocio.",
};

export const Route = createFileRoute("/tarjeta-nfc-qr")({
  component: TarjetaNfcPage,
  head: () =>
    crearHeadCategoriaSeo({
      path: config.path,
      title: "Tarjeta NFC para reseñas de Google en Chile | Pyme Visible",
      description:
        "Compra una tarjeta NFC para pedir reseñas de Google. Portátil, configurada y lista para usar en Chile por $10.000, sin mensualidades.",
      ogTitle: "Tarjeta NFC para reseñas de Google | Pyme Visible",
      ogDescription:
        "Tarjeta NFC portátil configurada con el perfil de Google de tu negocio y lista para usar.",
      image: modelos[0]?.image ?? "/productos/tarjetas-google.webp",
      breadcrumb: config.breadcrumb,
      products: modelos,
      faqs: config.faqs,
    }),
});

function TarjetaNfcPage() {
  return <CategoriaSeoPage config={config} />;
}
